import { UmbrellaType } from "./umbrella";

/**
 * Editorial content for the Umbrella Collections landing screen.
 *
 * The products API only returns `display_name`, `handle`, `image` and
 * `umbrella_type`, so the marketing copy, the "Best suited for" bullets and the
 * lifestyle hover photography live here until those fields are available from
 * the backend. Everything is looked up by `Collection.handle`, and every lookup
 * degrades gracefully: a product line with no entry simply renders without a
 * description and without the hover overlay.
 */

/**
 * How a studio render is framed inside a card's media panel.
 *
 * The renders are not shot to a common scale or horizon, so the design file
 * positions each one by hand: canopy bottoms line up across a row and every
 * umbrella fills a comparable share of its card. The three values are the
 * design's own placement, as percentages of the panel: `width` scales the
 * render off the panel's width (its height follows from the render's aspect
 * ratio), while `left` and `top` shift it. Values outside 0-100 overflow the
 * panel and are clipped, which is what trims the dead space around these
 * renders down to the framing the design specifies.
 *
 * Scaling off the panel's *width* is what makes the framing survive a resize.
 * The panel holds the design's aspect ratio at every viewport (see
 * `panelAspect` in CollectionMenuNew.vue), so a card that is 70% of its design
 * width gets a panel 70% as tall and a render scaled to 70% — the same crop,
 * just smaller. Scaling off the panel's height instead pins the render to an
 * absolute size, and a narrowing card then eats into the umbrella itself.
 */
export interface StudioCrop {
  /** Render width as a percentage of the panel width. */
  width: number;
  /** Horizontal offset as a percentage of the panel width; negative moves left. */
  left: number;
  /** Vertical offset as a percentage of the panel height; negative moves up. */
  top: number;
}

/**
 * How a lifestyle photo is framed inside a card's media panel on hover.
 *
 * The photos are all wider than the panel, so `object-fit: cover` crops them
 * horizontally and the full frame height is visible — which means
 * `object-position` has no vertical effect and cannot lift the subject. Growing
 * the element past the panel is what gives the crop somewhere to move: `height`
 * enlarges it (cover re-fits the photo to the taller box, zooming in) and `top`
 * slides it up so the subject clears the hover overlay. Keep
 * `height + top >= 100` or the photo stops short of the panel's bottom edge.
 */
export interface LifestyleCrop {
  /** Photo height as a percentage of the panel height. */
  height: number;
  /** Vertical offset as a percentage of the panel height; negative moves up. */
  top: number;
}

export interface ProductLineContent {
  /** Card heading. Falls back to `Collection.display_name` when omitted. */
  title: string;
  /** Short blurb rendered under the card heading. */
  description: string;
  /**
   * Bullets shown in the hover overlay, under a "Choose the <name> when you
   * want:" heading that is derived from `title` rather than repeated here.
   */
  chooseWhen: string[];
  /** Lifestyle photo revealed on hover, layered over the studio render. */
  lifestyleImage: string;
  /**
   * Per-photo framing for the hover state. Omit when the umbrellas already read
   * clearly above the overlay at the photo's natural `object-fit: cover`
   * framing, as they do for most of the shots.
   */
  lifestyleCrop?: LifestyleCrop;
  /**
   * Resting-state studio render from the design file. These are framed to fill
   * the card edge to edge, so they are rendered with `object-fit: cover`.
   * When omitted the card falls back to the product API's `Collection.image`,
   * which is a product on white and is contained rather than cropped.
   */
  studioImage?: string;
  /**
   * Per-render framing from the design file. Omit when the design file leaves
   * the render at its natural `object-fit: cover` framing, as it does for the
   * two Cantilever cards.
   */
  studioCrop?: StudioCrop;
}

export interface UmbrellaTypeContent {
  /** Label shown in the dark collection header bar. */
  label: string;
  description: string;
}

/** Header bar copy for each umbrella type, keyed by `Collection.umbrella_type`. */
export const umbrellaTypeContent: Partial<Record<
  UmbrellaType,
  UmbrellaTypeContent
>> = {
  [UmbrellaType.Cantilever]: {
    label: "Cantilever",
    description:
      "Cantilevers shift the support structure to the side, creating open, uninterrupted space beneath the canopy. This allows for greater flexibility of furniture placement and delivers a more architectural presence than traditional designs."
  },
  [UmbrellaType.Giant]: {
    label: "Giant",
    description:
      "Giant umbrellas are designed to cover larger footprints without the permanence of traditional shade structures. They also offer innovative telescoping and tilting systems for flexible coverage as the sun moves."
  },
  // The API calls this type "market"; the design labels it "Classic".
  [UmbrellaType.Market]: {
    label: "Classic",
    description:
      "Classic umbrellas feature traditional center-post designs, delivering dependable coverage, intuitive operation, and a versatile footprint to complement nearly any outdoor environment."
  }
};

/**
 * Builds a CDN URL for a collection photo.
 *
 * The photography lives on the CDN rather than in `src/assets` so the bundle
 * stays lean and the marketing team can reshoot without a redeploy. Both the
 * lifestyle photos and the `-studio` renders sit flat in the same folder, and
 * the environment's `VUE_APP_CDN_URL` picks staging or production.
 */
const collectionImage = (name: string): string =>
  `${process.env.VUE_APP_CDN_URL}/sd/collections_menu/${name}.jpg`;

/** Per-product-line content, keyed by `Collection.handle`. */
export const productLineContent: Record<string, ProductLineContent> = {
  eclipse: {
    title: "The Eclipse",
    description: "Precision-engineered for complete shade control.",
    chooseWhen: [
      "A premium outdoor environment with a refined architectural aesthetic",
      "Precise shade positioning and effortless adjustability",
      "Marine-grade aluminum construction and stainless steel components",
      "Frankford's most advanced cantilever umbrella"
    ],
    lifestyleImage: collectionImage("eclipse"),
    studioImage: collectionImage("eclipse-studio")
  },
  aurora: {
    title: "The Aurora",
    description:
      "Freedom to arrange, move, and adapt, featuring aluminum or fiberglass ribs.",
    chooseWhen: [
      "All the benefits of a cantilever umbrella in a versatile, adaptable design",
      "Open space and flexible furniture layouts underneath the shade",
      "Durability and everyday performance",
      "Premium shade with outstanding versatility and value"
    ],
    lifestyleImage: collectionImage("aurora"),
    studioImage: collectionImage("aurora-studio")
  },
  nova: {
    title: "The Nova",
    description:
      "Expansive commercial-grade coverage, supported by a 4-inch mast and reinforced extruded aluminum ribs.",
    chooseWhen: [
      "Maximum coverage with minimal umbrella count",
      "A large-format solution semi- and non-permanent installation options",
      "Heavy-duty commercial performance",
      "A statement-making presence"
    ],
    lifestyleImage: collectionImage("nova"),
    studioImage: collectionImage("nova-studio"),
    studioCrop: { width: 145.08, left: -22.58, top: -18.36 }
  },
  greenwich_g_series: {
    title: "The Giant Greenwich",
    description:
      "Clean aluminum aesthetics at scale, featuring a polished silver anodized finish and marine chrome-plated finial.",
    chooseWhen: [
      "Expansive shade with elevated style",
      "A refined, resort-inspired aesthetic",
      "Lightweight operation and corrosion resistant materials",
      "Maximum coverage without visual heaviness"
    ],
    lifestyleImage: collectionImage("greenwich_g_series"),
    studioImage: collectionImage("greenwich_g_series-studio"),
    studioCrop: { width: 216.47, left: -58.21, top: -4.72 }
  },
  monterey_g_series: {
    title: "The Giant Monterey",
    description:
      "Our renowned fiberglass design made large scale, featuring a single 2-inch aluminum center post and ¾-inch thick fiberglass ribs.",
    chooseWhen: [
      "Expansive shade with enhanced wind performance",
      "The proven performance of fiberglass construction",
      "Dependable commercial shade in challenging conditions",
      "Large-scale coverage built to perform season after season"
    ],
    lifestyleImage: collectionImage("monterey_g_series"),
    studioImage: collectionImage("monterey_g_series-studio"),
    studioCrop: { width: 216.47, left: -59.34, top: -5.56 }
  },
  greenwich: {
    title: "The Greenwich",
    description:
      "Elevated aesthetics meets premium aluminum construction, creating a timeless silhouette.",
    chooseWhen: [
      "The clean, refined look of a polished aluminum frame",
      "Lightweight operation and corrosion resistant materials",
      "A contemporary, minimalist aesthetic",
      "Commercial durability with a sleek architectural presence"
    ],
    lifestyleImage: collectionImage("greenwich"),
    studioImage: collectionImage("greenwich-studio"),
    studioCrop: { width: 316.31, left: -107.88, top: -12.5 }
  },
  monterey_pulley_lift: {
    title: "The Monterey Pulley Lift",
    description:
      "Intelligent fiberglass construction that flexes with the elements, featuring an easy-glide pulley system to open and close the canopy.",
    chooseWhen: [
      "Simple, dependable manual operation",
      "Fiberglass construction for enhanced wind resilience",
      "Long-term durability and ease of maintenance"
    ],
    lifestyleImage: collectionImage("monterey_pulley_lift"),
    studioImage: collectionImage("monterey_pulley_lift-studio"),
    studioCrop: { width: 277.5, left: -86.42, top: -3.31 }
  },
  monterey_crank: {
    title: "The Monterey Crank",
    description:
      "Intelligent fiberglass construction that flexes with the elements, featuring easy operation with our exclusive commercial-grade crank system.",
    chooseWhen: [
      "The strength of the Monterey with easier everyday operation",
      "An umbrella that will be opened and closed frequently",
      "Smooth, effortless functionality"
    ],
    lifestyleImage: collectionImage("monterey_crank"),
    // The umbrella row sits low in this shot — roughly 56%–85% down the frame —
    // so at the natural framing the overlay covers it completely. Lifting it
    // clears the canopies at the narrowest card width, where the overlay is
    // tallest because its bullets wrap onto more lines. Much past 200 and the
    // canopy tops start clipping against the top edge of the panel.
    lifestyleCrop: { height: 190, top: -90 },
    studioImage: collectionImage("monterey_crank-studio"),
    studioCrop: { width: 276.15, left: -88.08, top: 0 }
  },
  monterey_auto_tilt: {
    title: "The Monterey Crank + Auto Tilt",
    description:
      "Intelligent fiberglass construction that flexes with the elements, including our commercial-grade crank system plus a 30° tilt function.",
    chooseWhen: [
      "Shade that adapts throughout the day",
      "Maximum comfort and coverage as the sun moves",
      "Easy crank operation and automatic tilt functionality",
      "Frankford's most versatile Classic umbrella."
    ],
    lifestyleImage: collectionImage("monterey_auto_tilt"),
    studioImage: collectionImage("monterey_auto_tilt-studio"),
    studioCrop: { width: 276.15, left: -88.08, top: 0 }
  }
};
